import socialClient from './social-client';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface MetaPage {
    id: number;                  // chatmeta_pages.id (page_pk)
    page_id: string;             // ID do Facebook
    page_name: string;
    picture_url?: string | null;
    account_id?: number | null;  // chatmeta_oauth_accounts.id
    has_valid_token: boolean;
}

export interface MetaAccount {
    id: number;
    meta_user_id: string;
    meta_user_name?: string | null;
    picture_url?: string | null;
    pages_count: number;
    last_sync_at?: string | null;
}

export interface PersonaJson {
    tone?: string;
    language?: string;
    voice?: string;
    hashtags?: string[];
    [key: string]: unknown;
}

export interface SocialPageConfig {
    id: number;
    user_id: number;
    page_pk: number;
    persona_name?: string | null;
    persona_json?: PersonaJson | null;
    logo_asset_id?: number | null;
    model_photo_asset_id?: number | null;
    project_id?: number | null;
    story_schedule_times?: string[] | null;
    post_schedule_times?: string[] | null;
    schedule_timezone: string;
    horizon_days: number;
    max_per_day: number;
    default_post_destination: string;
    default_story_destination: string;
    status: string;
    // enriquecido do chatmeta_pages
    page_name?: string | null;
    page_picture_url?: string | null;
    page_id_external?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface SocialPageConfigCreate {
    page_pk: number;
    persona_name?: string | null;
    persona_json?: PersonaJson;
    logo_asset_id?: number | null;
    model_photo_asset_id?: number | null;
    project_id?: number | null;
    story_schedule_times?: string[];
    post_schedule_times?: string[];
    schedule_timezone?: string;
    horizon_days?: number;
    max_per_day?: number;
    default_post_destination?: string;
    default_story_destination?: string;
    status?: string;
}

export interface SocialRssFeed {
    id: number;
    user_id: number;
    feed_url: string;
    name?: string | null;
    cta_url_override?: string | null;
    custom_prompt?: string | null;
    last_seen_guid?: string | null;
    last_polled_at?: string | null;
    last_error?: string | null;
    status: string;
    page_config_ids: number[];
    created_at?: string | null;
    updated_at?: string | null;
}

export interface SocialFeedItem {
    id: number;
    feed_id: number;
    guid: string;
    title?: string | null;
    link?: string | null;
    content_summary?: string | null;
    image_url?: string | null;
    published_at?: string | null;
    seen_at?: string | null;
    status: string;
}

export interface SocialPost {
    id: number;
    page_config_id: number;
    feed_item_id?: number | null;
    type: string;
    ai_text?: string | null;
    ai_image_url?: string | null;
    ai_video_url?: string | null;
    cta_destination?: string | null;
    cta_url?: string | null;
    scheduled_at: string;
    schedule_slot_hhmm: string;
    text_provider?: string | null;
    text_model?: string | null;
    image_provider?: string | null;
    image_model?: string | null;
    status: string;
    fb_post_id?: string | null;
    fb_post_url?: string | null;
    error_code?: number | null;
    error_message?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface SocialAsset {
    id: number;
    user_id: number;
    kind: string;
    file_path: string;
    file_url: string;
    mime_type?: string | null;
    width?: number | null;
    height?: number | null;
    sha256?: string | null;
    created_at?: string | null;
}

export interface GenericResponse {
    success: boolean;
    message?: string | null;
    data?: unknown;
}

// ─── Meta accounts + pages ────────────────────────────────────────────────────

export const listMetaAccounts = async (): Promise<MetaAccount[]> => {
    const r = await socialClient.get<MetaAccount[]>('/meta-accounts');
    return r.data;
};

export const listMetaPages = async (params?: { account_id?: number }): Promise<MetaPage[]> => {
    const r = await socialClient.get<MetaPage[]>('/meta-pages', { params });
    return r.data;
};

// ─── Pages config ─────────────────────────────────────────────────────────────

export const listPagesConfig = async (params?: {
    project_id?: number;
    status?: string;
}): Promise<SocialPageConfig[]> => {
    const r = await socialClient.get<SocialPageConfig[]>('/pages-config', { params });
    return r.data;
};

export const getPageConfig = async (id: number): Promise<SocialPageConfig> => {
    const r = await socialClient.get<SocialPageConfig>(`/pages-config/${id}`);
    return r.data;
};

export const createPageConfig = async (payload: SocialPageConfigCreate): Promise<SocialPageConfig> => {
    const r = await socialClient.post<SocialPageConfig>('/pages-config', payload);
    return r.data;
};

export const updatePageConfig = async (
    id: number, payload: Partial<SocialPageConfigCreate>,
): Promise<SocialPageConfig> => {
    const r = await socialClient.patch<SocialPageConfig>(`/pages-config/${id}`, payload);
    return r.data;
};

export const deletePageConfig = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.delete<GenericResponse>(`/pages-config/${id}`);
    return r.data;
};

export interface SyncPageResponse extends GenericResponse {
    data?: {
        enqueued: number;
        reason?:
            | 'queue_full'
            | 'no_feeds'
            | 'pulling_feeds'
            | 'no_items_after_retries'
            | 'enqueued'
            | 'config_not_found';
        need_feed?: number;
        need_story?: number;
        current_free?: { feed?: number; story?: number };
        target_per_type?: number;
        feeds_polled?: number;
        retry_in_sec?: number;
        retry_num?: number;
    } | null;
}

export const syncPageNow = async (
    id: number, fill_to?: number,
): Promise<SyncPageResponse> => {
    const r = await socialClient.post<SyncPageResponse>(
        `/pages-config/${id}/sync-now`,
        undefined,
        { params: fill_to ? { fill_to } : undefined },
    );
    return r.data;
};

// ─── RSS feeds (catálogo global) ──────────────────────────────────────────────

export const listRssFeeds = async (): Promise<SocialRssFeed[]> => {
    const r = await socialClient.get<SocialRssFeed[]>('/rss-feeds');
    return r.data;
};

export const createRssFeed = async (payload: {
    feed_url: string;
    name?: string;
    cta_url_override?: string | null;
    custom_prompt?: string | null;
    status?: string;
    page_config_ids?: number[];
}): Promise<SocialRssFeed> => {
    const r = await socialClient.post<SocialRssFeed>('/rss-feeds', payload);
    return r.data;
};

export const updateRssFeed = async (
    id: number,
    payload: Partial<{
        feed_url: string;
        name: string;
        cta_url_override: string | null;
        custom_prompt: string | null;
        status: string;
        page_config_ids: number[];
    }>,
): Promise<SocialRssFeed> => {
    const r = await socialClient.patch<SocialRssFeed>(`/rss-feeds/${id}`, payload);
    return r.data;
};

export const deleteRssFeed = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.delete<GenericResponse>(`/rss-feeds/${id}`);
    return r.data;
};

export const listFeedItems = async (
    feed_id: number, limit = 50,
): Promise<SocialFeedItem[]> => {
    const r = await socialClient.get<SocialFeedItem[]>(`/rss-feeds/${feed_id}/items`, {
        params: { limit },
    });
    return r.data;
};

export const pollFeedNow = async (feed_id: number): Promise<GenericResponse> => {
    const r = await socialClient.post<GenericResponse>(`/rss-feeds/${feed_id}/poll-now`);
    return r.data;
};

// ─── Vinculação feeds ↔ pages (M×N) ───────────────────────────────────────────

export const listFeedsForPage = async (page_config_id: number): Promise<SocialRssFeed[]> => {
    const r = await socialClient.get<SocialRssFeed[]>(`/pages-config/${page_config_id}/feeds`);
    return r.data;
};

export const setFeedsForPage = async (
    page_config_id: number, feed_ids: number[],
): Promise<SocialRssFeed[]> => {
    const r = await socialClient.put<SocialRssFeed[]>(`/pages-config/${page_config_id}/feeds`, {
        feed_ids,
    });
    return r.data;
};

export const linkFeedToPage = async (page_config_id: number, feed_id: number): Promise<GenericResponse> => {
    const r = await socialClient.post<GenericResponse>(`/pages-config/${page_config_id}/feeds/${feed_id}`);
    return r.data;
};

export const unlinkFeedFromPage = async (page_config_id: number, feed_id: number): Promise<GenericResponse> => {
    const r = await socialClient.delete<GenericResponse>(`/pages-config/${page_config_id}/feeds/${feed_id}`);
    return r.data;
};

// ─── Assets ───────────────────────────────────────────────────────────────────

export const listAssets = async (params?: {
    kind?: string; limit?: number;
}): Promise<SocialAsset[]> => {
    const r = await socialClient.get<SocialAsset[]>('/assets', { params });
    return r.data;
};

export const uploadAsset = async (file: File, kind: string): Promise<SocialAsset> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    const r = await socialClient.post<SocialAsset>('/assets/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
};

export const deleteAsset = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.delete<GenericResponse>(`/assets/${id}`);
    return r.data;
};

// ─── Posts ────────────────────────────────────────────────────────────────────

export const listPosts = async (params: {
    page_config_id: number;
    status?: string;
    type?: string;
    limit?: number;
    offset?: number;
}): Promise<SocialPost[]> => {
    const r = await socialClient.get<SocialPost[]>('/posts', { params });
    return r.data;
};

export const getPost = async (id: number): Promise<SocialPost> => {
    const r = await socialClient.get<SocialPost>(`/posts/${id}`);
    return r.data;
};

export const publishPostNow = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.post<GenericResponse>(`/posts/${id}/publish-now`);
    return r.data;
};

export const cancelPost = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.delete<GenericResponse>(`/posts/${id}`);
    return r.data;
};

export const regeneratePost = async (
    id: number,
    target: 'text' | 'image' | 'both',
): Promise<GenericResponse> => {
    const r = await socialClient.post<GenericResponse>(
        `/posts/${id}/regenerate`,
        undefined,
        { params: { target } },
    );
    return r.data;
};

export const enhancePostImage = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.post<GenericResponse>(`/posts/${id}/enhance-image`);
    return r.data;
};

export const generateVideoForPost = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.post<GenericResponse>(`/posts/${id}/generate-video`);
    return r.data;
};

export const generateTalkingVideoForPost = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.post<GenericResponse>(`/posts/${id}/generate-talking-video`);
    return r.data;
};

export const publishVideoNow = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.post<GenericResponse>(`/posts/${id}/publish-video-now`);
    return r.data;
};

export const reschedulePost = async (
    id: number, scheduled_at: string,
): Promise<GenericResponse> => {
    const r = await socialClient.put<GenericResponse>(
        `/posts/${id}/reschedule`,
        { scheduled_at },
    );
    return r.data;
};

// ─── Categorias ────────────────────────────────────────────────────────────

export interface MetaCategory {
    id: number;
    name: string;
    user_id: number;
    pages_count: number;
    valid_pages_count: number;
}

export interface SocialCategoryConfig {
    id: number;
    user_id: number;
    category_id: number;
    persona_name?: string | null;
    persona_json?: PersonaJson | null;
    logo_asset_id?: number | null;
    model_photo_asset_id?: number | null;
    story_schedule_times?: string[] | null;
    post_schedule_times?: string[] | null;
    schedule_timezone: string;
    horizon_days: number;
    max_per_day: number;
    default_post_destination: string;
    default_story_destination: string;
    status: string;
    // enriquecidos
    category_name?: string | null;
    pages_count?: number;
    valid_pages_count?: number;
    feed_ids?: number[];
    created_at?: string | null;
    updated_at?: string | null;
}

export interface SocialCategoryConfigCreate {
    category_id: number;
    persona_name?: string | null;
    persona_json?: PersonaJson;
    logo_asset_id?: number | null;
    model_photo_asset_id?: number | null;
    story_schedule_times?: string[];
    post_schedule_times?: string[];
    schedule_timezone?: string;
    horizon_days?: number;
    max_per_day?: number;
    default_post_destination?: string;
    default_story_destination?: string;
    status?: string;
}

export const listCategories = async (): Promise<MetaCategory[]> => {
    const r = await socialClient.get<MetaCategory[]>('/categories');
    return r.data;
};

export const listCategoryConfigs = async (): Promise<SocialCategoryConfig[]> => {
    const r = await socialClient.get<SocialCategoryConfig[]>('/category-configs');
    return r.data;
};

export const getCategoryConfig = async (id: number): Promise<SocialCategoryConfig> => {
    const r = await socialClient.get<SocialCategoryConfig>(`/category-configs/${id}`);
    return r.data;
};

export const createCategoryConfig = async (
    payload: SocialCategoryConfigCreate,
): Promise<SocialCategoryConfig> => {
    const r = await socialClient.post<SocialCategoryConfig>('/category-configs', payload);
    return r.data;
};

export const updateCategoryConfig = async (
    id: number, payload: Partial<SocialCategoryConfigCreate>,
): Promise<SocialCategoryConfig> => {
    const r = await socialClient.patch<SocialCategoryConfig>(`/category-configs/${id}`, payload);
    return r.data;
};

export const deleteCategoryConfig = async (id: number): Promise<GenericResponse> => {
    const r = await socialClient.delete<GenericResponse>(`/category-configs/${id}`);
    return r.data;
};

export const getCategoryFeeds = async (id: number): Promise<number[]> => {
    const r = await socialClient.get<number[]>(`/category-configs/${id}/feeds`);
    return r.data;
};

export const setCategoryFeeds = async (
    id: number, feed_ids: number[],
): Promise<number[]> => {
    const r = await socialClient.put<number[]>(`/category-configs/${id}/feeds`, {
        feed_ids,
    });
    return r.data;
};

export interface CategorySyncAllResponse extends GenericResponse {
    data?: {
        per_page: Array<{
            page_pk: number;
            page_name?: string | null;
            page_config_id?: number;
            enqueued: number;
            reason?: string;
        }>;
        pages_total: number;
        pages_enqueued: number;
        pages_pulling: number;
        pages_queue_full: number;
        pages_no_token: number;
        total_enqueued: number;
    } | null;
}

export const syncAllPagesForCategory = async (
    config_id: number, fill_to?: number,
): Promise<CategorySyncAllResponse> => {
    const r = await socialClient.post<CategorySyncAllResponse>(
        `/category-configs/${config_id}/sync-all-pages`,
        undefined,
        { params: fill_to ? { fill_to } : undefined },
    );
    return r.data;
};

// Páginas de uma categoria com page_config_id + contagem de posts (pra ver
// os posts de cada página a partir da tela da categoria).
export interface SocialCategoryPage {
    page_pk: number;
    page_id: string | null;
    page_name: string | null;
    picture_url: string | null;
    has_valid_token: boolean;
    page_config_id: number | null;
    posts_count: number;
}

export const listCategoryPages = async (
    config_id: number,
): Promise<SocialCategoryPage[]> => {
    const r = await socialClient.get<SocialCategoryPage[]>(
        `/category-configs/${config_id}/pages`,
    );
    return r.data;
};

// Pré-check de saúde da página (probe vivo no Graph API + histórico de restrição)
// — pra ver antes de sincronizar/gerar e não gastar token/geração à toa.
export type PageHealthStatus = 'ok' | 'restrita' | 'sem_token' | 'token_invalido' | 'erro';
export interface SocialPageHealth {
    page_pk: number;
    page_id: string;
    page_name: string | null;
    picture_url: string | null;
    status: PageHealthStatus;
    detail: string | null;
    can_publish: boolean;
}

export const listCategoryPagesHealth = async (
    config_id: number,
): Promise<SocialPageHealth[]> => {
    const r = await socialClient.get<SocialPageHealth[]>(
        `/category-configs/${config_id}/pages/health`,
    );
    return r.data;
};
